import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import {
  Button,
  Container,
  Divider,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Modal,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AssessmentType, createAssessment } from '../../redux/reducers/assessmentReducer';
import { selectCustomFrameworks, selectStandardFrameworks } from '../../redux/reducers/frameworkReducer';

function validate(details) {
  return details.name !== '' && Object.keys(details.framework).length !== 0;
}

function CreateAssessmentModal({ handleClose }) {
  const initialAssessmentDetails = {
    name: '',
    assessmentType: AssessmentType.AUDIT,
    framework: {},
    description: '',
  };
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [assessmentDetails, setAssessmentDetails] = useState(initialAssessmentDetails);
  const [activeframework, setFramework] = useState('');
  const [validationError, setValidationError] = useState(false);

  const listAssessmentType = AssessmentType.list();
  const listStandardFrameworks = useSelector(selectStandardFrameworks);
  const listCustomFrameworks = useSelector(selectCustomFrameworks);

  const handleInitialize = () => {
    setAssessmentDetails({ ...initialAssessmentDetails });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    const newDetails = { ...assessmentDetails };
    newDetails[name] = value;
    setAssessmentDetails(newDetails);
    setUnsavedChanges(true);
  };

  const handleChangeType = (event) => {
    const newDetails = { ...assessmentDetails };
    newDetails.assessmentType = event.target.value;
    setAssessmentDetails(newDetails);
    setUnsavedChanges(true);
  };

  const handleChangeFramework = ({ target }) => {
    setFramework(target.value);
    const newDetails = { ...assessmentDetails };
    newDetails.framework = target.value;
    setAssessmentDetails(newDetails);
    setUnsavedChanges(true);
  };

  const handleSave = () => {
    if (validate(assessmentDetails)) {
      dispatch(createAssessment(assessmentDetails));
      handleClose();
    } else {
      setValidationError(true);
    }
  };

  const handleUnsavedChanges = (_, reason) => {
    if (unsavedChanges) {
      switch (reason) {
        case 'backdropClick':
          // Prevent outside click modal close
          break;
        default:
          setUnsavedChanges(false);
          handleInitialize();
          handleSave();
          break;
      }
    } else {
      handleInitialize();
      handleClose();
    }
  };

  const handleCancel = () => {
    handleInitialize();
    handleClose();
  };

  useEffect(() => {
    handleInitialize();
  }, []);

  return (
    <Modal open onClose={handleUnsavedChanges}>
      <Container variant="modal" disableGutters>
        <Stack variant="modalTop" spacing={2}>
          <Typography variant="h6">{t('assessment.modal.buttonCreateNew')}</Typography>
          <TextField
            required
            variant="outlined"
            subtype="signinField"
            onChange={handleChange}
            id="name"
            name="name"
            InputLabelProps={{
              shrink: true,
            }}
            label={t('assessment.modal.name')}
            value={assessmentDetails.name}
          />
          {validationError && <Typography variant="error">{t('error.emptyField')}</Typography>}
          <FormControl>
            <InputLabel shrink sx={{ px: 1, backgroundColor: 'white' }} htmlFor="label">
              {`${t('assessment.modal.type')} *`}
            </InputLabel>
            <Select
              id='assement-type-dropdown'
              variant="outlined"
              value={assessmentDetails.assessmentType}
              onChange={(e) => handleChangeType(e)}
            >
              <ListSubheader>{t('assessment.modal.type')}</ListSubheader>
              {listAssessmentType.map((assessmentType) => (
                <MenuItem id={assessmentType} key={assessmentType} value={assessmentType}>
                  {t(`assessment.type.${assessmentType}`)}
                </MenuItem>
              ))}
            </Select>
            {validationError && <Typography variant="error">{t('error.emptyField')}</Typography>}
          </FormControl>
          <FormControl>
            <InputLabel shrink sx={{ px: 1, backgroundColor: 'white' }} htmlFor="label">
              {`${t('assessment.modal.framework.title')} *`}
            </InputLabel>
            <Select id='framework-dropdown' variant="outlined" value={activeframework} onChange={handleChangeFramework}>
              <ListSubheader>{t('assessment.modal.framework.title')}</ListSubheader>
              {listStandardFrameworks.map((framework) => (
                <MenuItem id={`select-${framework.title}`} key={framework.id} value={framework.id}>
                  {framework.title}
                </MenuItem>
              ))}
              <Divider />
              {listCustomFrameworks.length !== 0 && (
                <ListSubheader>{t('assessment.modal.framework.custom')}</ListSubheader>
              )}
              {listCustomFrameworks.map((framework) => (
                <MenuItem id={`select-${framework.title}`} key={framework.id} value={framework.id}>
                  {framework.title}
                </MenuItem>
              ))}
            </Select>
            {validationError && <Typography variant="error">{t('error.emptyField')}</Typography>}
          </FormControl>
          <TextField
            required
            variant="outlined"
            subtype="signinField"
            onChange={handleChange}
            id="description"
            name="description"
            InputLabelProps={{
              shrink: true,
            }}
            label={t('assessment.modal.description')}
            multiline
            rows={6}
            value={assessmentDetails.description}
          />
        </Stack>
        <Stack variant="modalButtons" direction="row" justifyContent="flex-end" spacing={1}>
          <Button id='cancel' variant="text" startIcon={<ClearIcon />} onClick={() => handleCancel()}>
            {t('assessment.modal.buttonCancel')}
          </Button>
          {!assessmentDetails.name || !assessmentDetails.assessmentType || !activeframework ? (
            <Button id='create' disabled variant="contained" startIcon={<AddIcon />}>
              {t('assessment.modal.buttonCreateNew')}
            </Button>
          ) : (
            <Button id='save' variant="contained" startIcon={<SaveIcon />} onClick={() => handleSave()}>
              {t('assessment.modal.buttonSaveNew')}
            </Button>
          )}
        </Stack>
      </Container>
    </Modal>
  );
}

export default CreateAssessmentModal;
